import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { urls } from './config.dev';
import Topbar from './Topbar';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css'; 

function LabDashboard2() {
  const [pendingLabTestsCount, setPendingLabTestsCount] = useState(0);
  const [employeeName, setEmployeeName] = useState('');
  const [stats, setStats] = useState({ year: 0, month: 0, week: 0, pending: 0 });
  const [labTestData, setLabTestData] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));  
  const [endDate, setEndDate] = useState(new Date()); 
  const [minAge, setMinAge] = useState(0);
  const [maxAge, setMaxAge] = useState(130);
  const navigate = useNavigate();
  
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  
  useEffect(() => {
    const scrollWidth = document.documentElement.scrollWidth - window.innerWidth;
    window.scrollTo(scrollWidth / 2, 0);
  }, []);
  
  useEffect(() => {
    const fetchTokenAndCheckSecurity = async () => {
      try {
        const securityResponse = await fetch(urls.security, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: urlToken }),
        });

        if (securityResponse.ok) {
          const securityData = await securityResponse.json();

          if (securityData.message === 'Session valid') {
            setEmployeeName(securityData.employee_name);
            fetchLabStats(urlToken); 
            fetchLabTestsData(urlToken, startDate, endDate, minAge, maxAge); 
          } else if (securityData.error === 'Session expired') {
            navigate(`/dashboard?token=${securityData.clinic_session_token}`);
          } else {
            navigate('/login');
          }
        } else {
          throw new Error('Failed to perform security check');
        }
      } catch (error) {
        console.error('Error performing security check:', error);
        navigate('/login');
      }
    };

    fetchTokenAndCheckSecurity();
  }, [navigate, startDate, endDate, minAge, maxAge]);

  const fetchLabStats = async (token) => {
    try {
      const response = await fetch(urls.labtestscount, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
  
      if (response.ok) {
        const data = await response.json();
        
        setStats({
          year: data.total_year,     
          month: data.total_month,   
          week: data.total_week,     
          today: data.total_today,   
          pending: data.total_pending 
        });
      } else {
        throw new Error('Failed to fetch lab stats');
      }
    } catch (error) {
      console.error('Error fetching lab stats:', error);
    }
  };
  
  const fetchLabTestsData = async (token, startDate, endDate, minAge, maxAge) => {
    try {
      const response = await fetch(urls.labstat2, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          minAge,
          maxAge
        }),
      });
  
      if (response.ok) {
        const data = await response.json();
        const filteredTests = Object.entries(data).filter(([key, test]) => test.total_positive > 0);
        setLabTestData(filteredTests);
      } else {
        throw new Error('Failed to fetch lab test details');
      }
    } catch (error) {
      console.error('Error fetching lab test details:', error);
    }
  };

  const handleRefreshData = () => {
    fetchLabStats(urlToken);
  };
  
  useEffect(() => {
    const intervalId = setInterval(() => {
      handleRefreshData();
    }, 10000);
  
    return () => clearInterval(intervalId);
  }, []);
  
  const handleNavigateToRecentTests = () => {
    navigate(`/labTests?token=${urlToken}`);
  };
  
  // Professional color scheme
  const colors = {
    primary: '#2c3e50',
    secondary: '#3498db',
    accent: '#e74c3c',
    lightBg: '#f8f9fa',
    darkText: '#2c3e50',
    lightText: '#7f8c8d',
    border: '#dfe6e9',
    success: '#27ae60',
    warning: '#f39c12',
    info: '#2980b9'
  };

  return (
    <div style={{
      backgroundColor: colors.lightBg,
      minHeight: '100vh',
      paddingBottom: '50px'
    }}>
      <Topbar token={urlToken} />
      
      {/* Main Content Container */}
      <div style={{
      
        margin: '0 auto',
        padding: '0 20px',
        marginTop: '90px'
      }}>
        
        {/* Stats Cards Section */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {Object.entries(stats)
            .filter(([key]) => key !== 'pending')
            .map(([key, value]) => (
              <div
                key={key}
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  padding: '25px 15px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                  textAlign: 'center',
                  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                
                  '&:hover': {
                    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <div style={{ 
                  fontSize: '16px',
                  fontWeight: '600',
                  color: colors.lightText,
                  marginBottom: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {key.replace('_', ' ')}
                </div>
                <div style={{ 
                  fontSize: '32px',
                  fontWeight: '700',
                  color: colors.darkText
                }}>
                  {value}
                </div>
              </div>
            ))}
          
          {/* Pending Tests Card */}
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '25px 15px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              borderTop: `4px solid ${colors.accent}`,
              cursor: 'pointer',
              animation: stats.pending > 0 ? 'pulse 1.5s infinite' : 'none',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)'
              }
            }}
            onClick={() => navigate(`/lab?token=${urlToken}`)}
          >
            <div style={{ 
              fontSize: '16px',
              fontWeight: '600',
              color: colors.lightText,
              marginBottom: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Waiting Patients
            </div>
            <div style={{ 
              fontSize: '32px',
              fontWeight: '700',
              color: colors.accent
            }}>
              {stats.pending}
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '25px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
          marginBottom: '30px'
        }}>
          <h2 style={{ 
            fontSize: '22px',
            fontWeight: '600',
            color: colors.darkText,
            marginBottom: '25px',
            textAlign: 'center',
            borderBottom: `1px solid ${colors.border}`,
            paddingBottom: '15px'
          }}>
            LABORATORY STATISTICS FROM {startDate.toLocaleDateString()} TO {endDate.toLocaleDateString()}
            <div style={{ fontSize: '16px', fontWeight: '400', color: colors.lightText, marginTop: '5px' }}>
              Age range: {minAge} - {maxAge} years
            </div>
          </h2>

          <div style={{ 
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '20px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ 
                marginRight: '10px',
                fontSize: '14px',
                fontWeight: '500',
                color: colors.darkText,
                minWidth: '80px'
              }}>
                Start Date:
              </label>
              <DatePicker 
                selected={startDate} 
                onChange={(date) => setStartDate(date)}
                className="custom-datepicker"
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ 
                marginRight: '10px',
                fontSize: '14px',
                fontWeight: '500',
                color: colors.darkText,
                minWidth: '80px'
              }}>
                End Date:
              </label>
              <DatePicker 
                selected={endDate} 
                onChange={(date) => setEndDate(date)}
                className="custom-datepicker"
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ 
                marginRight: '10px',
                fontSize: '14px',
                fontWeight: '500',
                color: colors.darkText
              }}>
                Age Range:
              </label>
              <input
                type="number"
                value={minAge}
                onChange={(e) => setMinAge(Number(e.target.value))}
                style={{
                  width: '60px',
                  padding: '8px 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  marginRight: '5px',
                  textAlign: 'center'
                }}
              />
              <span style={{ margin: '0 5px' }}>-</span>
              <input
                type="number"
                value={maxAge}
                onChange={(e) => setMaxAge(Number(e.target.value))}
                style={{
                  width: '60px',
                  padding: '8px 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  textAlign: 'center'
                }}
              />
            </div>
            
            <button
              onClick={handleNavigateToRecentTests}
              style={{
                padding: '10px 20px',
                backgroundColor: colors.info,
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#1a6fa3'
                }
              }}
            >
              <span style={{ marginRight: '8px' }}>📋</span>
              View Recent Tests
            </button>
          </div>
        </div>

        {/* Lab Test Data Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}>
          <div style={{
            overflowX: 'auto',
            padding: '0'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '800px'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: colors.primary,
                  color: 'white'
                }}>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    fontWeight: '500',
                    fontSize: '14px',
                    letterSpacing: '0.5px'
                  }}>Test Name</th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontSize: '14px',
                    letterSpacing: '0.5px'
                  }}>Total Tests</th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontSize: '14px',
                    letterSpacing: '0.5px'
                  }}>Positive Cases</th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontSize: '14px',
                    letterSpacing: '0.5px'
                  }}>Males Positive</th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontSize: '14px',
                    letterSpacing: '0.5px'
                  }}>Females Positive</th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontSize: '14px',
                    letterSpacing: '0.5px'
                  }}>Most Positive Ages</th>
                </tr>
              </thead>
              <tbody>
                {labTestData.map(([testName, test], index) => (
                  <tr 
                    key={index} 
                    style={{
                      backgroundColor: index % 2 === 0 ? 'white' : colors.lightBg,
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f0f3f7'
                      }
                    }}
                  >
                    <td style={{
                      padding: '14px 20px',
                      borderBottom: `1px solid ${colors.border}`,
                      fontWeight: '500',
                      color: colors.darkText
                    }}>
                      {testName}
                    </td>
                    <td style={{
                      padding: '14px 20px',
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: 'center',
                      color: colors.lightText
                    }}>
                      {test.total_tests}
                    </td>
                    <td style={{
                      padding: '14px 20px',
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: 'center',
                      color: colors.accent,
                      fontWeight: '600'
                    }}>
                      {test.total_positive}
                    </td>
                    <td style={{
                      padding: '14px 20px',
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: 'center',
                      color: colors.darkText
                    }}>
                      {test.male_count}
                    </td>
                    <td style={{
                      padding: '14px 20px',
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: 'center',
                      color: colors.darkText
                    }}>
                      {test.female_count}
                    </td>
                    <td style={{
                      padding: '14px 20px',
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: 'center',
                      color: colors.darkText,
                      minWidth: '150px'
                    }}>
                      {test.most_common_age && test.most_common_age.length > 0 ? 
                        test.most_common_age.join(', ') : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.03); }
            100% { transform: scale(1); }
          }
          
          .custom-datepicker {
            padding: 8px 12px;
            border: 1px solid ${colors.border};
            border-radius: 4px;
            font-size: 14px;
            width: 150px;
          }
          
          .custom-datepicker:focus {
            outline: none;
            border-color: ${colors.secondary};
            box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
          }
        `}
      </style>
    </div>
  );
}

export default LabDashboard2;